import { Sparkle } from "@phosphor-icons/react";

/** AI HTML email generator modal. Self-contained — receives state + handlers
 * from the parent CampaignComposer so behavior stays identical to the inline
 * version that previously lived in CampaignComposer.jsx. */
export default function AIComposeModal({
    open,
    onClose,
    topic, setTopic,
    keywords, setKeywords,
    audience, setAudience,
    tone, setTone,
    cta, setCta,
    color, setColor,
    length, setLength,
    busy,
    onGenerate,
}) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ai-modal">
            <div className="tech-card w-full max-w-2xl max-h-[90vh] overflow-y-auto !border-purple-500/40">
                <div className="flex items-center justify-between p-5 border-b border-slate-800">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-purple-300 font-mono-alt flex items-center gap-1">
                            <Sparkle size={12} weight="fill" /> Claude Sonnet 4.5
                        </div>
                        <h3 className="font-heading text-xl font-bold text-slate-50 mt-1">Compose with AI</h3>
                        <p className="text-xs text-slate-400 mt-1">Generate a polished HTML email from a short brief. Replaces subject + HTML body + plaintext.</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-100 text-2xl" data-testid="ai-modal-close">×</button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="overline block mb-2">Topic / what should this email say? <span className="text-red-400">*</span></label>
                        <textarea rows={3} value={topic} onChange={(e) => setTopic(e.target.value)}
                            placeholder="e.g. Reach out to Texas land surveyors who haven't tried our boundary survey CAD drafting service yet — emphasize 48-hour turnaround and 330k+ projects delivered."
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-purple-500 resize-y"
                            data-testid="ai-topic" />
                    </div>
                    <div>
                        <label className="overline block mb-2">Keywords (comma-separated, optional)</label>
                        <input type="text" value={keywords} onChange={(e) => setKeywords(e.target.value)}
                            placeholder="boundary survey, ALTA/NSPS, 48hr turnaround, free trial"
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-purple-500"
                            data-testid="ai-keywords" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-2">Audience</label>
                            <input type="text" value={audience} onChange={(e) => setAudience(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-purple-500" />
                        </div>
                        <div>
                            <label className="overline block mb-2">Tone</label>
                            <select value={tone} onChange={(e) => setTone(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-purple-500"
                                data-testid="ai-tone">
                                <option value="professional">Professional</option>
                                <option value="friendly">Friendly</option>
                                <option value="warm">Warm</option>
                                <option value="urgent">Urgent</option>
                                <option value="confident">Confident</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline block mb-2">Call-to-action</label>
                            <input type="text" value={cta} onChange={(e) => setCta(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-purple-500" />
                        </div>
                        <div>
                            <label className="overline block mb-2">Length</label>
                            <select value={length} onChange={(e) => setLength(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-purple-500">
                                <option value="short">Short (~100 words)</option>
                                <option value="medium">Medium (~200 words)</option>
                                <option value="long">Long (~300 words)</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="overline block mb-2">Primary color</label>
                        <div className="flex gap-2 items-center">
                            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                                className="w-12 h-9 rounded border border-slate-700 bg-slate-900" />
                            <input type="text" value={color} onChange={(e) => setColor(e.target.value)}
                                className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm font-mono-alt focus:outline-none focus:border-purple-500" />
                        </div>
                    </div>
                    <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-3 text-[11px] text-purple-200/80 leading-relaxed">
                        ⚡ Generation typically takes 8-15 seconds. The result will replace the current subject + HTML body —
                        save a draft first if you want to keep what's there.
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-800">
                    <button onClick={onClose} className="btn-ghost">Cancel</button>
                    <button
                        onClick={onGenerate}
                        disabled={busy || !topic.trim()}
                        className="btn-primary !bg-purple-500 hover:!bg-purple-400 !border-purple-400 disabled:opacity-50"
                        data-testid="ai-generate-btn"
                    >
                        <Sparkle size={16} weight="fill" />
                        {busy ? "Generating…" : "Generate email"}
                    </button>
                </div>
            </div>
        </div>
    );
}
